import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, TextField, Button, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Paper, Alert, Snackbar, Divider, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useApp } from '../context/AppContext';
import { projectsApi, Environment } from '../api/projects';

export default function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentProject, selectProject, loadProjects } = useApp();
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (projectId) {
      selectProject(projectId);
      projectsApi.getEnvironments(projectId).then(setEnvironments).catch(() => {});
    }
  }, [projectId, selectProject]);

  const handleAddEnv = async () => {
    if (!projectId || !newKey.trim() || !newName.trim()) return;
    try {
      const env = await projectsApi.createEnvironment(projectId, { key: newKey.trim(), name: newName.trim(), description: newDesc.trim() });
      setEnvironments(prev => [...prev, env]);
      setNewKey(''); setNewName(''); setNewDesc('');
      await loadProjects();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add environment');
    }
  };

  const handleDeleteEnv = async (envKey: string) => {
    if (!projectId) return;
    try {
      await projectsApi.deleteEnvironment(projectId, envKey);
      setEnvironments(prev => prev.filter(e => e.key !== envKey));
      await loadProjects();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete environment');
    }
  };

  const copyApiKey = () => {
    if (currentProject?.apiKey) {
      navigator.clipboard.writeText(currentProject.apiKey);
      setCopied(true);
    }
  };

  const sdkSnippet = `// Single environment
const flags = await fetch('https://haki.kotahirau.com/v1/flags?env=prod', {
  headers: { 'x-api-key': '${currentProject?.apiKey || '<your-api-key>'}' }
}).then(r => r.json());

// All environments
const allFlags = await fetch('https://haki.kotahirau.com/v1/flags/all', {
  headers: { 'x-api-key': '${currentProject?.apiKey || '<your-api-key>'}' }
}).then(r => r.json());

if (flags['my-feature']) { /* ... */ }`;

  return (
    <Box maxWidth={800}>
      <Typography variant="h5" fontWeight={700} mb={3}>Project Settings</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>API Key</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth value={currentProject?.apiKey || ''} size="small"
              slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } } }}
            />
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyApiKey} size="small">
              Copy
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Environments</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Key</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {[...environments].sort((a, b) => a.order - b.order).map(env => (
                  <TableRow key={env.key}>
                    <TableCell><Chip label={env.key} size="small" sx={{ fontFamily: 'monospace' }} /></TableCell>
                    <TableCell>{env.name}</TableCell>
                    <TableCell>{env.description}</TableCell>
                    <TableCell>{env.order}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => handleDeleteEnv(env.key)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" fontWeight={600} mb={1}>Add Environment</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Key" value={newKey} onChange={e => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} size="small" sx={{ width: 120 }} />
            <TextField label="Name" value={newName} onChange={e => setNewName(e.target.value)} size="small" sx={{ flex: 1 }} />
            <TextField label="Description" value={newDesc} onChange={e => setNewDesc(e.target.value)} size="small" sx={{ flex: 2 }} />
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddEnv} disabled={!newKey || !newName}>
              Add
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>SDK Integration</Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.900', color: 'grey.100', p: 2, borderRadius: 1,
              fontSize: 12, overflow: 'auto', fontFamily: 'monospace',
            }}
          >
            {sdkSnippet}
          </Box>
        </CardContent>
      </Card>

      <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)} message="API key copied!" />
    </Box>
  );
}
